// web/js/player-sync-settings/SyncSettings.tsx
//
// Collapsible "Sync Settings" panel shown above the comments area when
// more than one column is open. Contains three toggles that are all on by
// default. The panel is rendered into the global `#player-sync-settings-
// container` DOM slot (see player.php:770) by the
// `syncPlayerSyncSettingsVisibilityToColumnCount` helper in
// html/js/player_utils.js. Actual sync behavior is not wired yet — this
// is UI scaffolding only.

import React, {useEffect, useId, useState} from 'react';

declare module 'react' {
  namespace JSX {
    interface IntrinsicElements {
      'check-box': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement> & {
        checked?: boolean;
        text?: string;
      }, HTMLElement>;
    }
  }
}

export interface SyncSettingsProps {
  initialExpanded?: boolean;
  initialSyncPosition?: boolean;
  initialAudioFollowsSelection?: boolean;
  initialCommentsFollowSelection?: boolean;
}

function SyncSettings({
  initialExpanded = false,
  initialSyncPosition = true,
  initialAudioFollowsSelection = true,
  initialCommentsFollowSelection = true,
}: SyncSettingsProps): React.ReactElement {
  const [expanded, setExpanded] = useState(initialExpanded);
  const [syncPosition, setSyncPosition] = useState(() => getSavedSetting("player.sync.position", initialSyncPosition));
  const [audioFollows, setAudioFollows] = useState(() => getSavedSetting("player.sync.audioFollows", initialAudioFollowsSelection));
  const [commentsFollow, setCommentsFollow] = useState(() => getSavedSetting("player.sync.commentsFollow", initialCommentsFollowSelection));
  // Sync settings to localStorage and notify player_utils to re-evaluate
  useEffect(() => {
    try { localStorage.setItem('player.sync.position', String(syncPosition)); } catch {}
    window.dispatchEvent(new Event('kollab:sync-settings-changed'));
  }, [syncPosition]);

  useEffect(() => {
    try { localStorage.setItem('player.sync.audioFollows', String(audioFollows)); } catch {}
    window.dispatchEvent(new Event('kollab:sync-settings-changed'));
  }, [audioFollows]);

  useEffect(() => {
    try { localStorage.setItem('player.sync.commentsFollow', String(commentsFollow)); } catch {}
    window.dispatchEvent(new Event('kollab:sync-settings-changed'));
  }, [commentsFollow]);

  const panelId = useId();

  const toggleExpanded = () => setExpanded((v) => !v);

  return (
    <div className="player-sync-settings">
      <button
        type="button"
        className="player-sync-settings-title"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={toggleExpanded}
      >
        <span className="mr-4" aria-hidden="true">{expanded ? '▼' : '▶'}</span>
        <span>Sync Settings</span>
      </button>
      {expanded && (
        <div id={panelId} className="player-sync-settings-panel" role="group" aria-label="Sync Settings">
          <check-box className="player-sync-settings-option" checked={syncPosition} text="Sync position" onChange={(e) => setSyncPosition((e.target as HTMLInputElement).checked)} />
          <check-box className="player-sync-settings-option" checked={audioFollows} text="Audio follows selection" onChange={(e) => setAudioFollows((e.target as HTMLInputElement).checked)} />
          <check-box className="player-sync-settings-option" checked={commentsFollow} text="Comments follow selection" onChange={(e) => setCommentsFollow((e.target as HTMLInputElement).checked)} />
        </div>
      )}
    </div>
  );
}

function getSavedSetting(key: string, defaultValue: boolean): boolean {
  try {
    const val = localStorage.getItem(key);
    if (val === null) return defaultValue;
    return val !== 'false';
  } catch {
    return defaultValue;
  }
}

export default SyncSettings;
